import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Center, Loader, Paper, Text, Title } from "@mantine/core";
import {
  IconHistory,
  IconNews,
  IconSettings,
  IconTool,
  IconUsers,
  type Icon,
} from "@tabler/icons-react";
import { useMe } from "../api/queries";
import { atLeast, type Role } from "../api/types";
import { Header } from "../ui/Header";
import { Shell } from "../ui/Shell";
import { NavBar } from "../ui/NavBar";
import { useLink, usePath } from "../router";
import { AuditPage } from "./AuditPage";
import { MaintenancePage } from "./MaintenancePage";
import { NewsAdminPage } from "./NewsAdminPage";
import { SettingsPage } from "./SettingsPage";
import { UsersPage } from "./UsersPage";
import { useLayout } from "../ui/useLayout";

interface AdminTab {
  path: string;
  /** A catalogue key, not copy: `tabsFor` is pure and the tab is rendered somewhere else. */
  labelKey: string;
  /** The mark before the label. A component, not an element: this table is data. */
  icon: Icon;
  minimum: Role;
}

/**
 * The sections, each with the role that may reach it.
 *
 * The minimum is not the security boundary -- every route behind these enforces its own role
 * server-side. It is what a tab is *for*: nothing here has a read below `admin`, so offering
 * the tabs to an editor would be an invitation to four refusals. Their surface is review
 * mode, which is a mode of the archive page and not one of these.
 *
 * `/admin` itself is Settings, which is also where an unrecognised `/admin/*` path lands --
 * named rather than reached as `TABS[0]`, which is an index this file cannot prove exists.
 */
const SETTINGS: AdminTab = {
  path: "/admin",
  labelKey: "adminApp.tabSettings",
  icon: IconSettings,
  minimum: "admin",
};

const TABS: readonly AdminTab[] = [
  SETTINGS,
  { path: "/admin/news", labelKey: "adminApp.tabNews", icon: IconNews, minimum: "admin" },
  { path: "/admin/users", labelKey: "adminApp.tabUsers", icon: IconUsers, minimum: "admin" },
  {
    path: "/admin/maintenance",
    labelKey: "adminApp.tabMaintenance",
    icon: IconTool,
    minimum: "admin",
  },
  { path: "/admin/audit", labelKey: "adminApp.tabAudit", icon: IconHistory, minimum: "admin" },
];

export function tabsFor(role: Role): readonly AdminTab[] {
  return TABS.filter((tab) => atLeast(role, tab.minimum));
}

export function AdminApp() {
  const path = usePath();
  const { data: me, isLoading, isError } = useMe();
  const { t } = useTranslation("admin");
  // Four labelled sections do not fit a phone row, and the bar does not wrap: the marks
  // carry them, the same trade the header's own bar makes one band above this one.
  const { phone } = useLayout();
  if (isLoading)
    return (
      <AdminFrame>
        <Center h="100%">
          <Loader size="lg" />
        </Center>
      </AdminFrame>
    );

  // A failed `/api/auth/me` is not a signed-out visitor. Both arrive here as no user, and
  // telling someone with a live session to sign in is the page confidently stating the one
  // thing it does not know.
  if (isError) {
    return (
      <Notice heading={t("adminApp.unreachableHeading")} body={t("adminApp.unreachableBody")} />
    );
  }

  // Not a security boundary -- every route enforces its own role server-side, and hiding a
  // button has never stopped anyone. This is so a visitor who follows a link they cannot use
  // sees an explanation instead of a page of failed requests.
  if (!me) {
    return <Notice heading={t("adminApp.signInHeading")} body={t("adminApp.signInBody")} />;
  }

  const tab = TABS.find((entry) => entry.path === path) ?? SETTINGS;
  if (!atLeast(me.role, tab.minimum)) {
    return (
      <Notice
        heading={t("adminApp.forbiddenHeading", { section: t(tab.labelKey) })}
        body={t("adminApp.forbiddenBody", { needed: tab.minimum, yours: me.role })}
      />
    );
  }

  return (
    <Shell>
      {/* Both bands in one header row: page-level navigation belongs inside `<header>`, and
          admin then has the archive's three rows with the third empty and collapsed to
          nothing. */}
      <Shell.Header>
        <Header />
        {/* The value is the *resolved* tab rather than the raw path, so an unrecognised
            `/admin/*` -- which renders Settings -- lights Settings rather than nothing. */}
        <NavBar
          label={t("adminApp.sectionsLabel")}
          value={tab.path}
          px={phone ? 12 : 20}
          // The bar's bottom edge is `Shell.Header`'s; this is the rule between the header
          // row and the sections under it.
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          {tabsFor(me.role).map((entry) => (
            <Tab
              key={entry.path}
              path={entry.path}
              label={t(entry.labelKey)}
              icon={entry.icon}
              compact={phone}
              current={tab.path}
            />
          ))}
        </NavBar>
      </Shell.Header>

      <Shell.Main>
        {path === "/admin/news" ? (
          <NewsAdminPage />
        ) : path === "/admin/users" ? (
          <UsersPage />
        ) : path === "/admin/maintenance" ? (
          <MaintenancePage />
        ) : path === "/admin/audit" ? (
          <AuditPage />
        ) : (
          <SettingsPage />
        )}
      </Shell.Main>
    </Shell>
  );
}

/**
 * The frame an admin page draws even when it has nothing to show.
 *
 * The header rides along: a bare spinner or a bare sentence on an otherwise empty page has no
 * wordmark, no account menu and no way back, and a slow or restarting API left it that way
 * indefinitely.
 */
function AdminFrame({ children }: { children: ReactNode }) {
  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main>{children}</Shell.Main>
    </Shell>
  );
}

/** One sentence where a section would have been: why this page is not the page asked for. */
function Notice({ heading, body }: { heading: string; body: string }) {
  return (
    <AdminFrame>
      <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
        <Title order={2} fz="lg" lh={1.5} mb={6}>
          {heading}
        </Title>
        <Text size="sm" my={14}>
          {body}
        </Text>
      </Paper>
    </AdminFrame>
  );
}

function Tab({
  path,
  label,
  icon: Icon,
  compact,
  current,
}: {
  path: string;
  label: string;
  icon: Icon;
  compact: boolean;
  current: string;
}) {
  const link = useLink(path);
  return (
    <NavBar.Slot
      value={path}
      href={link.href}
      onClick={link.onClick}
      active={current === path}
      icon={<Icon size={15} stroke={1.5} aria-hidden="true" />}
      label={label}
      compact={compact}
    />
  );
}
